import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRegistrationStatistic } from '../../api';
import '../../App.css';

const RegistrationStatistic = () => {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [lastUpdated, setLastUpdated] = useState(null);

    // Chart & Filter state
    const [timeframe, setTimeframe] = useState('all'); // 'all', '30d', '7d'
    const [chartMode, setChartMode] = useState('daily'); // 'daily', 'cumulative'
    const [hoveredBar, setHoveredBar] = useState(null);

    // Table state
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortOrder, setSortOrder] = useState('newest'); // 'newest', 'oldest'

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const res = await getRegistrationStatistic();
            setData(res);
            setLastUpdated(new Date().toLocaleTimeString());
        } catch (err) {
            console.error('Error fetching registration statistics:', err);
            setError(err.response?.data?.message || 'Failed to fetch registration statistics.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }
        fetchData();
    }, [navigate, fetchData]);

    // Process dates from byDate
    // byDate format: { "2026-07-30": 1, "2026-07-29": 2, ... }
    const sortedDailyData = useMemo(() => {
        if (!data?.byDate) return [];

        const entries = Object.entries(data.byDate).map(([dateStr, count]) => ({
            dateStr,
            date: new Date(dateStr),
            count
        }));

        // Sort ascending chronologically by date
        entries.sort((a, b) => a.date - b.date);

        return entries;
    }, [data]);

    // Filtered data based on timeframe
    const filteredChartData = useMemo(() => {
        if (sortedDailyData.length === 0) return [];

        if (timeframe === 'all') return sortedDailyData;

        const now = new Date();
        const cutoff = new Date();
        if (timeframe === '30d') {
            cutoff.setDate(now.getDate() - 30);
        } else if (timeframe === '7d') {
            cutoff.setDate(now.getDate() - 7);
        }

        return sortedDailyData.filter(item => item.date >= cutoff);
    }, [sortedDailyData, timeframe]);

    // Compute cumulative data if chartMode === 'cumulative'
    const chartData = useMemo(() => {
        if (chartMode === 'daily') return filteredChartData;

        let runningTotal = 0;
        return filteredChartData.map(item => {
            runningTotal += item.count;
            return {
                ...item,
                displayCount: runningTotal
            };
        });
    }, [filteredChartData, chartMode]);

    // Statistical Insights
    const metrics = useMemo(() => {
        const total = data?.total || 0;
        const activeDays = sortedDailyData.length;

        let peakDay = { dateStr: 'N/A', count: 0 };
        sortedDailyData.forEach(item => {
            if (item.count > peakDay.count) {
                peakDay = { dateStr: item.dateStr, count: item.count };
            }
        });

        // Last 30 days total
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const last30DaysCount = sortedDailyData
            .filter(item => item.date >= thirtyDaysAgo)
            .reduce((acc, curr) => acc + curr.count, 0);

        return {
            total,
            activeDays,
            peakDay,
            last30DaysCount
        };
    }, [data, sortedDailyData]);

    // Filtered & Sorted Users List for Table
    const filteredUsers = useMemo(() => {
        if (!data?.users) return [];
        let list = [...data.users];

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            list = list.filter(u =>
                (u.name && u.name.toLowerCase().includes(term)) ||
                (u.email && u.email.toLowerCase().includes(term)) ||
                (u.id && u.id.toLowerCase().includes(term))
            );
        }

        list.sort((a, b) => {
            const dateA = new Date(a.created_at || a.createdAt);
            const dateB = new Date(b.created_at || b.createdAt);
            return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
        });

        return list;
    }, [data, searchTerm, sortOrder]);

    // Pagination
    const totalPages = Math.ceil(filteredUsers.length / pageSize) || 1;
    const paginatedUsers = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredUsers.slice(start, start + pageSize);
    }, [filteredUsers, currentPage, pageSize]);

    // Reset pagination when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, pageSize, sortOrder]);

    const token = localStorage.getItem('token');
    if (!token) return null;

    // SVG Chart dimensions
    const maxVal = Math.max(...chartData.map(d => chartMode === 'daily' ? d.count : (d.displayCount || d.count)), 1);

    return (
        <div style={{
            minHeight: '100vh',
            background: '#ccdff5',
            padding: '40px 20px',
            fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>
            <div style={{
                maxWidth: '1400px',
                margin: '0 auto',
                background: '#F6F6F6',
                borderRadius: '20px',
                padding: '40px',
                boxShadow: '0 4px 24px rgba(44, 62, 80, 0.08)'
            }}>
                {/* Header & Breadcrumbs */}
                <div style={{
                    marginBottom: '24px',
                    borderBottom: '2px solid #e2e8f0',
                    paddingBottom: '20px'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '12px'
                    }}>
                        <button
                            onClick={() => navigate('/dashboard')}
                            style={{
                                background: '#ffffff',
                                border: '1px solid #cbd5e1',
                                borderRadius: '8px',
                                padding: '6px 14px',
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                color: '#3c5a5d',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = '#ffffff'; }}
                        >
                            <span>←</span> Back to Dashboard
                        </button>
                    </div>

                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '16px'
                    }}>
                        <div>
                            <h1 style={{
                                color: '#3c5a5d',
                                margin: '0 0 6px 0',
                                fontSize: '2.4rem',
                                fontWeight: '800'
                            }}>
                                Registration Statistics & Analytics
                            </h1>
                            <p style={{ margin: 0, color: '#707F81', fontSize: '1rem', fontWeight: '500' }}>
                                User sign-up trends, daily distribution, and registered member records
                            </p>
                        </div>

                        <button
                            onClick={fetchData}
                            disabled={loading}
                            style={{
                                background: '#3c5a5d',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '10px',
                                padding: '10px 18px',
                                fontSize: '0.95rem',
                                fontWeight: '600',
                                cursor: loading ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                opacity: loading ? 0.7 : 1,
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = '#2c4345'; }}
                            onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = '#3c5a5d'; }}
                        >
                            <span style={{
                                display: 'inline-block',
                                transform: loading ? 'rotate(360deg)' : 'none',
                                transition: 'transform 1s linear'
                            }}>🔄</span>
                            {loading ? 'Refreshing...' : 'Refresh Data'}
                        </button>
                    </div>
                </div>

                {/* Last updated notice */}
                {lastUpdated && (
                    <div style={{ color: '#707F81', fontSize: '0.85rem', marginBottom: '20px' }}>
                        Data updated at <strong>{lastUpdated}</strong>
                    </div>
                )}

                {/* Error Banner */}
                {error && (
                    <div style={{
                        color: '#dc2626',
                        marginBottom: '24px',
                        padding: '16px 20px',
                        background: '#fee2e2',
                        borderLeft: '4px solid #dc2626',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        fontWeight: '500',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <span>{error}</span>
                        <button
                            onClick={fetchData}
                            style={{
                                background: '#dc2626',
                                color: '#fff',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: '600'
                            }}
                        >
                            Retry
                        </button>
                    </div>
                )}

                {/* Content Area */}
                {loading && !data ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '80px 20px',
                        color: '#707F81',
                        fontSize: '1.1rem',
                        fontWeight: '500'
                    }}>
                        <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>📈</div>
                        Loading registration statistics...
                    </div>
                ) : (
                    <>
                        {/* Summary Cards */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                            gap: '20px',
                            marginBottom: '32px'
                        }}>
                            {/* Card 1: Total Registrations */}
                            <div style={{
                                background: '#ffffff',
                                borderRadius: '16px',
                                padding: '24px',
                                boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                                border: '1px solid #e2e8f0',
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    position: 'absolute', top: 0, left: 0, right: 0, height: '4px',
                                    background: 'linear-gradient(90deg, #3b82f6, #60a5fa)'
                                }} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <span style={{ color: '#707F81', fontSize: '0.85rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Total Users
                                    </span>
                                    <span style={{ fontSize: '1.4rem', background: '#eff6ff', padding: '8px', borderRadius: '10px' }}>👥</span>
                                </div>
                                <div style={{ fontSize: '2.4rem', fontWeight: '800', color: '#0c1415', marginBottom: '4px' }}>
                                    {metrics.total.toLocaleString()}
                                </div>
                                <div style={{ color: '#707F81', fontSize: '0.85rem' }}>
                                    All-time total user signups
                                </div>
                            </div>

                            {/* Card 2: Signup Days */}
                            <div style={{
                                background: '#ffffff',
                                borderRadius: '16px',
                                padding: '24px',
                                boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                                border: '1px solid #e2e8f0',
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    position: 'absolute', top: 0, left: 0, right: 0, height: '4px',
                                    background: 'linear-gradient(90deg, #10b981, #34d399)'
                                }} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <span style={{ color: '#707F81', fontSize: '0.85rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Active Signup Days
                                    </span>
                                    <span style={{ fontSize: '1.4rem', background: '#ecfdf5', padding: '8px', borderRadius: '10px' }}>📅</span>
                                </div>
                                <div style={{ fontSize: '2.4rem', fontWeight: '800', color: '#0c1415', marginBottom: '4px' }}>
                                    {metrics.activeDays} <span style={{ fontSize: '1.1rem', color: '#707F81', fontWeight: '600' }}>days</span>
                                </div>
                                <div style={{ color: '#707F81', fontSize: '0.85rem' }}>
                                    Days with at least 1 registration
                                </div>
                            </div>

                            {/* Card 3: Peak Single Day */}
                            <div style={{
                                background: '#ffffff',
                                borderRadius: '16px',
                                padding: '24px',
                                boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                                border: '1px solid #e2e8f0',
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    position: 'absolute', top: 0, left: 0, right: 0, height: '4px',
                                    background: 'linear-gradient(90deg, #8b5cf6, #a78bfa)'
                                }} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <span style={{ color: '#707F81', fontSize: '0.85rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Peak Day Signups
                                    </span>
                                    <span style={{ fontSize: '1.4rem', background: '#f5f3ff', padding: '8px', borderRadius: '10px' }}>🚀</span>
                                </div>
                                <div style={{ fontSize: '2.4rem', fontWeight: '800', color: '#0c1415', marginBottom: '4px' }}>
                                    {metrics.peakDay.count} <span style={{ fontSize: '1rem', color: '#8b5cf6', fontWeight: '700' }}>users</span>
                                </div>
                                <div style={{ color: '#707F81', fontSize: '0.85rem' }}>
                                    Record on {metrics.peakDay.dateStr}
                                </div>
                            </div>

                            {/* Card 4: Last 30 Days */}
                            <div style={{
                                background: '#ffffff',
                                borderRadius: '16px',
                                padding: '24px',
                                boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                                border: '1px solid #e2e8f0',
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    position: 'absolute', top: 0, left: 0, right: 0, height: '4px',
                                    background: 'linear-gradient(90deg, #f59e0b, #fbbf24)'
                                }} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <span style={{ color: '#707F81', fontSize: '0.85rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Last 30 Days
                                    </span>
                                    <span style={{ fontSize: '1.4rem', background: '#fffbeb', padding: '8px', borderRadius: '10px' }}>📊</span>
                                </div>
                                <div style={{ fontSize: '2.4rem', fontWeight: '800', color: '#0c1415', marginBottom: '4px' }}>
                                    {metrics.last30DaysCount.toLocaleString()}
                                </div>
                                <div style={{ color: '#707F81', fontSize: '0.85rem' }}>
                                    Signups in recent 30-day window
                                </div>
                            </div>
                        </div>

                        {/* Interactive Registration Chart Section */}
                        <div style={{
                            background: '#ffffff',
                            borderRadius: '16px',
                            padding: '28px',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                            border: '1px solid #e2e8f0',
                            marginBottom: '32px'
                        }}>
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: '16px',
                                marginBottom: '24px'
                            }}>
                                <div>
                                    <h2 style={{
                                        color: '#3c5a5d',
                                        fontSize: '1.4rem',
                                        fontWeight: '700',
                                        margin: '0 0 4px 0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px'
                                    }}>
                                        📊 Registration Trend Chart
                                    </h2>
                                    <p style={{ margin: 0, color: '#707F81', fontSize: '0.9rem' }}>
                                        {chartMode === 'daily' ? 'Daily new user registrations over time' : 'Cumulative total user growth curve'}
                                    </p>
                                </div>

                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                                    {/* Chart Mode Toggle */}
                                    <div style={{
                                        display: 'flex',
                                        background: '#f1f5f9',
                                        padding: '3px',
                                        borderRadius: '10px',
                                        border: '1px solid #e2e8f0'
                                    }}>
                                        <button
                                            onClick={() => setChartMode('daily')}
                                            style={{
                                                background: chartMode === 'daily' ? '#3c5a5d' : 'transparent',
                                                color: chartMode === 'daily' ? '#ffffff' : '#64748b',
                                                border: 'none',
                                                borderRadius: '8px',
                                                padding: '6px 14px',
                                                fontSize: '0.85rem',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            Daily
                                        </button>
                                        <button
                                            onClick={() => setChartMode('cumulative')}
                                            style={{
                                                background: chartMode === 'cumulative' ? '#3c5a5d' : 'transparent',
                                                color: chartMode === 'cumulative' ? '#ffffff' : '#64748b',
                                                border: 'none',
                                                borderRadius: '8px',
                                                padding: '6px 14px',
                                                fontSize: '0.85rem',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            Cumulative
                                        </button>
                                    </div>

                                    {/* Timeframe Filter */}
                                    <div style={{
                                        display: 'flex',
                                        background: '#f1f5f9',
                                        padding: '3px',
                                        borderRadius: '10px',
                                        border: '1px solid #e2e8f0'
                                    }}>
                                        <button
                                            onClick={() => setTimeframe('all')}
                                            style={{
                                                background: timeframe === 'all' ? '#3b82f6' : 'transparent',
                                                color: timeframe === 'all' ? '#ffffff' : '#64748b',
                                                border: 'none',
                                                borderRadius: '8px',
                                                padding: '6px 12px',
                                                fontSize: '0.85rem',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            All Time
                                        </button>
                                        <button
                                            onClick={() => setTimeframe('30d')}
                                            style={{
                                                background: timeframe === '30d' ? '#3b82f6' : 'transparent',
                                                color: timeframe === '30d' ? '#ffffff' : '#64748b',
                                                border: 'none',
                                                borderRadius: '8px',
                                                padding: '6px 12px',
                                                fontSize: '0.85rem',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            Last 30 Days
                                        </button>
                                        <button
                                            onClick={() => setTimeframe('7d')}
                                            style={{
                                                background: timeframe === '7d' ? '#3b82f6' : 'transparent',
                                                color: timeframe === '7d' ? '#ffffff' : '#64748b',
                                                border: 'none',
                                                borderRadius: '8px',
                                                padding: '6px 12px',
                                                fontSize: '0.85rem',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            Last 7 Days
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Chart Display */}
                            {chartData.length === 0 ? (
                                <div style={{
                                    padding: '60px',
                                    textAlign: 'center',
                                    color: '#94a3b8',
                                    fontSize: '0.95rem'
                                }}>
                                    No registration records available for the selected timeframe.
                                </div>
                            ) : (
                                <div style={{ position: 'relative', width: '100%', overflowX: 'auto' }}>
                                    <div style={{ minWidth: '700px', padding: '10px 0 20px 0' }}>
                                        {/* Chart container */}
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'flex-end',
                                            height: '260px',
                                            gap: chartData.length > 40 ? '3px' : chartData.length > 20 ? '8px' : '16px',
                                            paddingBottom: '30px',
                                            borderBottom: '2px solid #e2e8f0',
                                            position: 'relative'
                                        }}>
                                            {/* Background Horizontal Gridlines */}
                                            {[0, 0.25, 0.5, 0.75, 1].map((pct) => (
                                                <div
                                                    key={pct}
                                                    style={{
                                                        position: 'absolute',
                                                        left: 0,
                                                        right: 0,
                                                        bottom: `${30 + pct * 220}px`,
                                                        borderTop: '1px stroke-dasharray #f1f5f9',
                                                        borderTopStyle: 'dashed',
                                                        borderColor: '#e2e8f0',
                                                        zIndex: 0
                                                    }}
                                                />
                                            ))}

                                            {/* Bars */}
                                            {chartData.map((item, idx) => {
                                                const val = chartMode === 'daily' ? item.count : (item.displayCount || item.count);
                                                const heightPct = Math.max((val / maxVal) * 100, 4);
                                                const isHovered = hoveredBar?.dateStr === item.dateStr;

                                                return (
                                                    <div
                                                        key={item.dateStr || idx}
                                                        onMouseEnter={() => setHoveredBar({ ...item, val })}
                                                        onMouseLeave={() => setHoveredBar(null)}
                                                        style={{
                                                            flex: 1,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            height: '100%',
                                                            justifyContent: 'flex-end',
                                                            position: 'relative',
                                                            cursor: 'pointer',
                                                            zIndex: 1
                                                        }}
                                                    >
                                                        {/* Value label on hover or if small dataset */}
                                                        {(isHovered || chartData.length <= 15) && (
                                                            <span style={{
                                                                fontSize: '0.75rem',
                                                                fontWeight: '700',
                                                                color: isHovered ? '#3b82f6' : '#64748b',
                                                                marginBottom: '4px',
                                                                transition: 'all 0.2s'
                                                            }}>
                                                                {val}
                                                            </span>
                                                        )}

                                                        {/* Bar element */}
                                                        <div style={{
                                                            width: '100%',
                                                            maxWidth: chartData.length > 40 ? '16px' : '36px',
                                                            height: `${heightPct}%`,
                                                            background: isHovered
                                                                ? 'linear-gradient(180deg, #2563eb, #3b82f6)'
                                                                : 'linear-gradient(180deg, #3b82f6, #60a5fa)',
                                                            borderRadius: '6px 6px 2px 2px',
                                                            transition: 'all 0.2s ease',
                                                            boxShadow: isHovered ? '0 4px 12px rgba(59, 130, 246, 0.4)' : 'none',
                                                            transform: isHovered ? 'scaleY(1.03)' : 'none',
                                                            transformOrigin: 'bottom'
                                                        }} />

                                                        {/* Date label (X axis) */}
                                                        <span style={{
                                                            position: 'absolute',
                                                            bottom: '-24px',
                                                            fontSize: '0.7rem',
                                                            fontWeight: isHovered ? '700' : '500',
                                                            color: isHovered ? '#1e293b' : '#94a3b8',
                                                            whiteSpace: 'nowrap',
                                                            transform: chartData.length > 25 ? 'rotate(-45deg)' : 'none',
                                                            transformOrigin: 'top left'
                                                        }}>
                                                            {/* Short date format, e.g. 07/30 */}
                                                            {item.dateStr.slice(5)}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Hover Details Card / Floating Tooltip */}
                                    {hoveredBar && (
                                        <div style={{
                                            marginTop: '16px',
                                            background: '#eff6ff',
                                            border: '1px solid #bfdbfe',
                                            borderRadius: '12px',
                                            padding: '12px 20px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}>
                                            <div>
                                                <span style={{ fontSize: '0.9rem', color: '#1e40af', fontWeight: '600' }}>
                                                    📅 Date: <strong>{hoveredBar.dateStr}</strong>
                                                </span>
                                            </div>
                                            <div>
                                                <span style={{ fontSize: '0.95rem', color: '#1e3a8a', fontWeight: '700' }}>
                                                    {chartMode === 'daily' ? `Signups: ${hoveredBar.val}` : `Total Accounts: ${hoveredBar.val}`}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Registered Users Table Section */}
                        <div style={{
                            background: '#ffffff',
                            borderRadius: '16px',
                            padding: '28px',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                            border: '1px solid #e2e8f0'
                        }}>
                            {/* Table Header Controls */}
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: '16px',
                                marginBottom: '20px'
                            }}>
                                <div>
                                    <h2 style={{
                                        color: '#3c5a5d',
                                        fontSize: '1.3rem',
                                        fontWeight: '700',
                                        margin: '0 0 4px 0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        📋 Registered Accounts Directory ({filteredUsers.length})
                                    </h2>
                                    <p style={{ margin: 0, color: '#707F81', fontSize: '0.85rem' }}>
                                        Complete list of user accounts with registration timestamps
                                    </p>
                                </div>

                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                                    {/* Search Bar */}
                                    <div style={{ position: 'relative', minWidth: '240px' }}>
                                        <input
                                            type="text"
                                            placeholder="Search by name, email, ID..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '9px 14px 9px 36px',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: '10px',
                                                fontSize: '0.9rem',
                                                outline: 'none',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                        <span style={{
                                            position: 'absolute',
                                            left: '12px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            color: '#94a3b8',
                                            fontSize: '0.9rem'
                                        }}>
                                            🔍
                                        </span>
                                        {searchTerm && (
                                            <button
                                                onClick={() => setSearchTerm('')}
                                                style={{
                                                    position: 'absolute',
                                                    right: '10px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    background: 'transparent',
                                                    border: 'none',
                                                    color: '#94a3b8',
                                                    cursor: 'pointer',
                                                    fontWeight: '700'
                                                }}
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>

                                    {/* Sort Order Select */}
                                    <select
                                        value={sortOrder}
                                        onChange={(e) => setSortOrder(e.target.value)}
                                        style={{
                                            padding: '9px 12px',
                                            border: '1px solid #cbd5e1',
                                            borderRadius: '10px',
                                            fontSize: '0.85rem',
                                            background: '#ffffff',
                                            color: '#334155',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            outline: 'none'
                                        }}
                                    >
                                        <option value="newest">Newest First</option>
                                        <option value="oldest">Oldest First</option>
                                    </select>

                                    {/* Page Size Select */}
                                    <select
                                        value={pageSize}
                                        onChange={(e) => setPageSize(Number(e.target.value))}
                                        style={{
                                            padding: '9px 12px',
                                            border: '1px solid #cbd5e1',
                                            borderRadius: '10px',
                                            fontSize: '0.85rem',
                                            background: '#ffffff',
                                            color: '#334155',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            outline: 'none'
                                        }}
                                    >
                                        <option value={10}>10 / page</option>
                                        <option value={25}>25 / page</option>
                                        <option value={50}>50 / page</option>
                                    </select>
                                </div>
                            </div>

                            {/* Table */}
                            <div style={{ overflowX: 'auto', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                <table style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                    textAlign: 'left',
                                    fontSize: '0.9rem'
                                }}>
                                    <thead>
                                        <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                            <th style={{ padding: '14px 16px', color: '#64748b', fontWeight: '700', width: '50px' }}>#</th>
                                            <th style={{ padding: '14px 16px', color: '#64748b', fontWeight: '700' }}>User</th>
                                            <th style={{ padding: '14px 16px', color: '#64748b', fontWeight: '700' }}>Email</th>
                                            <th style={{ padding: '14px 16px', color: '#64748b', fontWeight: '700' }}>Registration Date</th>
                                            <th style={{ padding: '14px 16px', color: '#64748b', fontWeight: '700' }}>User ID</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedUsers.length === 0 ? (
                                            <tr>
                                                <td colSpan={5} style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>
                                                    No registered users found matching your search.
                                                </td>
                                            </tr>
                                        ) : (
                                            paginatedUsers.map((user, index) => {
                                                const rowNumber = (currentPage - 1) * pageSize + index + 1;
                                                const createdDate = user.created_at || user.createdAt;
                                                const formattedDate = createdDate
                                                    ? new Date(createdDate).toLocaleString(undefined, {
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })
                                                    : 'N/A';

                                                return (
                                                    <tr
                                                        key={user.id || index}
                                                        style={{
                                                            borderBottom: '1px solid #f1f5f9',
                                                            transition: 'background 0.15s'
                                                        }}
                                                        onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                                                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                                    >
                                                        <td style={{ padding: '14px 16px', color: '#94a3b8', fontWeight: '600' }}>
                                                            {rowNumber}
                                                        </td>
                                                        <td style={{ padding: '14px 16px', fontWeight: '700', color: '#1e293b' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                <div style={{
                                                                    width: '32px',
                                                                    height: '32px',
                                                                    borderRadius: '50%',
                                                                    background: '#e0f2fe',
                                                                    color: '#0369a1',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    fontWeight: '700',
                                                                    fontSize: '0.85rem'
                                                                }}>
                                                                    {(user.name || 'U').charAt(0).toUpperCase()}
                                                                </div>
                                                                <span>{user.name || 'Anonymous User'}</span>
                                                            </div>
                                                        </td>
                                                        <td style={{ padding: '14px 16px', color: '#334155' }}>
                                                            {user.email || 'N/A'}
                                                        </td>
                                                        <td style={{ padding: '14px 16px', color: '#475569', fontWeight: '500' }}>
                                                            {formattedDate}
                                                        </td>
                                                        <td style={{ padding: '14px 16px', color: '#94a3b8', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                                            {user.id ? `${user.id.slice(0, 13)}...` : 'N/A'}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination Controls */}
                            {totalPages > 1 && (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginTop: '20px',
                                    paddingTop: '16px',
                                    borderTop: '1px solid #f1f5f9'
                                }}>
                                    <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
                                        Showing <strong>{(currentPage - 1) * pageSize + 1}</strong> to <strong>{Math.min(currentPage * pageSize, filteredUsers.length)}</strong> of <strong>{filteredUsers.length}</strong> entries
                                    </div>

                                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                            disabled={currentPage === 1}
                                            style={{
                                                padding: '6px 12px',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: '8px',
                                                background: '#ffffff',
                                                color: currentPage === 1 ? '#cbd5e1' : '#334155',
                                                fontWeight: '600',
                                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                                fontSize: '0.85rem'
                                            }}
                                        >
                                            Previous
                                        </button>

                                        <span style={{ fontSize: '0.85rem', color: '#475569', padding: '0 8px', fontWeight: '600' }}>
                                            Page {currentPage} of {totalPages}
                                        </span>

                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                            disabled={currentPage === totalPages}
                                            style={{
                                                padding: '6px 12px',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: '8px',
                                                background: '#ffffff',
                                                color: currentPage === totalPages ? '#cbd5e1' : '#334155',
                                                fontWeight: '600',
                                                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                                fontSize: '0.85rem'
                                            }}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default RegistrationStatistic;
